"use client";

import { useRef, type ReactNode } from "react";
import { motion, useInView, useScroll, useSpring } from "framer-motion";
import ThemedCapture from "@/components/marketing/ThemedCapture";

/* ────────────────────────────────────────────────────────────────────────────
 *  PreviewSteps — the four things a visitor does, one per viewport, threaded
 *  on a single vertical rail.
 *
 *  Replaces the six-view carousel: a carousel asks the reader to operate it
 *  and shows six versions of ONE surface (the sidebar), which never explained
 *  the product as a sequence. These four are the actual path — find it on the
 *  map, read the number, open a layer, take the dossier away — and each gets
 *  a viewport of its own so nothing is skimmed.
 *
 *  Every image is a real capture of the same property, Laurierstraat 218-1
 *  (§Marketing claims), and all four come out of ONE run of
 *  scripts/capture-product-sidebar.ts — one enrichment, both themes reached
 *  through the app's own theme toggle. That is what makes the numbers on the
 *  map, the panel, the layer and the dossier cover agree with each other.
 *  Re-cut them together or not at all; capturing any of them in a second
 *  session re-runs the cascade and drifts the score by a point.
 *
 *  No score is typed into this file. The captures carry the numbers; copy
 *  that restated them would be a second source of truth to drift.
 * ──────────────────────────────────────────────────────────────────────────── */

export interface PreviewStep {
  /** Rail label — "01".."04". */
  n: string;
  eyebrow: string;
  title: string;
  body: string;
  /** Small print under the body. Optional. */
  note?: string;
  /** DARK capture path; ThemedCapture derives the -light sibling. */
  src: string;
  alt: string;
  width: number;
  height: number;
  /** Wide captures fill the column; tall ones are capped so a step still
   *  fits one viewport on a laptop. */
  shape: "wide" | "tall";
  /**
   * A composed figure instead of the single capture — the layer tabs, the
   * dossier fan. When set, `src`/`alt`/`width`/`height` are ignored (they are
   * still required so a step always has a plain capture to fall back to).
   */
  figure?: ReactNode;
  /**
   * How much of the capture actually carries content, in source pixels.
   *
   * The panel is shot at its full 1276 CSS px height with the footer pinned
   * to the bottom, so on a short view like the overview roughly 700 px of the
   * middle is empty — true to the product, but half an empty box as a figure.
   * Setting this crops the figure to the top `contentHeight` px. Layer tabs
   * fill their height and leave it unset.
   */
  contentHeight?: number;
}

export default function PreviewSteps({ steps }: { steps: PreviewStep[] }) {
  const railRef = useRef<HTMLDivElement>(null);
  // The rail fills as the reader descends. Bound to the steps block, not the
  // page, so it reads as progress THROUGH the sequence.
  const { scrollYProgress } = useScroll({
    target: railRef,
    offset: ["start 60%", "end 80%"],
  });
  const fill = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });

  return (
    <section className="border-t border-edge">
      <div className="max-w-[1160px] mx-auto px-5 sm:px-8">
        <div ref={railRef} className="relative">
          {/* ── The rail. `left-[7px]` puts the hairline through the centre of
                the 14 px step dots that hang off each text column. ── */}
          <div
            aria-hidden
            className="absolute left-[7px] top-3 bottom-3 w-px bg-edge"
          />
          <motion.div
            aria-hidden
            className="absolute left-[7px] top-3 bottom-3 w-px bg-accent origin-top"
            style={{ scaleY: fill }}
          />

          {steps.map((step) => (
            <Step key={step.n} step={step} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Step({ step }: { step: PreviewStep }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const tall = step.shape === "tall";
  /** The height actually shown — the crop when there is one, else the file's. */
  const shownHeight = step.contentHeight ?? step.height;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
      // Breathing room comes from PADDING, not from a min-height. A tall
      // `min-h` plus `items-center` centres a short step (the wide map) inside
      // its box, and the slack of two adjacent steps compounds into ~650 px of
      // dead rail between them. Sized by content, each step still lands at
      // roughly a viewport — the portrait captures are ~70svh on their own —
      // without the gaps.
      className="relative pl-10 sm:pl-16 py-16 sm:py-24 flex items-center"
    >
      <div className="grid lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] gap-10 lg:gap-16 items-center w-full">
        {/* ── Text, alongside the rail ── */}
        <div className="relative">
          {/* The dot sits back on the rail: the column starts at the step's
              pl-10/pl-16, so an equal negative offset lands on x = 0. It
              fills with the accent once the step is read, so the rail reads
              as a progress track rather than decoration. */}
          <motion.span
            aria-hidden
            className="absolute -left-10 sm:-left-16 top-[5px] h-3.5 w-3.5 rounded-full border-2 border-edge-strong bg-page"
            animate={inView ? { borderColor: "rgb(var(--c-accent))", backgroundColor: "rgb(var(--c-accent))" } : {}}
            transition={{ duration: 0.4, delay: 0.15 }}
          />
          <p className="font-data text-[0.65rem] text-ink-muted uppercase tracking-[0.2em] mb-4">
            <span className="text-accent-ink">{step.n}</span>
            <span aria-hidden className="mx-2 text-ink-muted">·</span>
            {step.eyebrow}
          </p>
          <h3 className="font-display text-[1.9rem] sm:text-[2.5rem] font-semibold text-ink leading-[1.1] tracking-[0.005em] mb-5">
            {step.title}
          </h3>
          <p className="text-[1rem] sm:text-[1.05rem] text-ink-2 leading-[1.7]">{step.body}</p>
          {step.note && (
            <p className="mt-4 text-[0.85rem] text-ink-muted leading-[1.6]">{step.note}</p>
          )}
        </div>

        {/* ── The capture ── */}
        <div className={tall ? "flex justify-center lg:justify-start" : ""}>
          {step.figure ?? (
          /* The figure's box is DEFINITE without the image: `aspect-ratio`
              plus one fixed dimension. Sizing it `w-fit` around an auto-width
              image collapsed it to 14 px of padding until the lazy image
              loaded — a big layout shift per step, and a permanent 14 px stub
              if a capture ever 404s.

              Which dimension is fixed flips at `lg`. Portrait captures have to
              be capped by HEIGHT on a desktop — the layer panel is 590×1568
              (0.376:1), so any shared width cap makes it ~900 px tall, past a
              viewport, while the wide map barely half-fills one. On a phone
              there is no height to spare, so width leads instead. */
          <figure
            className={`rounded-2xl border border-edge bg-surface p-1.5 sm:p-2 shadow-[0_40px_120px_-45px_rgb(0_0_0/0.55)] ${
              tall ? "w-full max-w-[290px] sm:max-w-[330px] lg:w-auto lg:max-w-none lg:h-[68svh]" : "w-full"
            }`}
            style={tall ? { aspectRatio: `${step.width} / ${shownHeight}` } : undefined}
          >
            <ThemedCapture
              src={step.src}
              alt={step.alt}
              width={step.width}
              height={shownHeight}
              // `contentHeight` crops a capture whose lower half is the panel's
              // empty middle — see the field's note. object-top keeps the crop
              // anchored to the part that speaks.
              style={step.contentHeight ? { objectFit: "cover", objectPosition: "top" } : undefined}
              sizes={tall ? "420px" : "(min-width: 1024px) 700px, 100vw"}
              // EVERY step stays lazy, including the first. ThemedCapture puts
              // both themes' <img> in the DOM and hides one in CSS, and the
              // hidden twin is only free while it is lazy — a display:none
              // lazy image is in no viewport, so it is never fetched. Marking
              // step 1 eager pulled BOTH step-map.jpg and step-map-light.jpg
              // (~1.7 MB for one visible picture); verified in the network log.
              // Nothing here is above the fold anyway — the header section
              // fills the first screen.
              loading="lazy"
              className={`rounded-xl ${tall ? "w-full h-full" : "w-full h-auto"}`}
            />
          </figure>
          )}
        </div>
      </div>
    </motion.div>
  );
}
